// Globals
import { TableColumnsType } from 'antd';
import { TFunction } from 'i18next';
import { Link } from 'react-router-dom';

// Models
import { ISuggestedFaq, ISuggestedFaqSourceChannel, SuggestedFaqStatus } from '@/models/suggestedFaq.model';

// Modules
import { FaqChannelsSelect } from '@/modules/FaqChannels';

// Components
import { ROUTES } from '@/constants/routes';
import { Button, Input, Select, TSelectOptions } from '@/ui';
import {
  StatusPill,
  StyledAnswer,
  StyledChannelTag,
  StyledDate,
  StyledQuestion,
  StyledSearchWrapper,
} from '../components';

// Helpers
import { DateFormats } from '@/constants/dates';
import { renderDate } from '@/utils';
import { TSuggestedFaqFilters } from './parseSuggestedFaqFilters';
import { getSuggestedFaqStatusLabel } from './getSuggestedFaqStatusLabel';

type TGetSuggestedFaqColumnsParams = {
  t: TFunction;
  filters?: TSuggestedFaqFilters;
  onFilterChange: <K extends keyof TSuggestedFaqFilters>(field: K, value: TSuggestedFaqFilters[K]) => void;
  statusOptions?: TSelectOptions<SuggestedFaqStatus>;
};

export const getSuggestedFaqColumns = ({
  t,
  filters,
  onFilterChange,
  statusOptions,
}: TGetSuggestedFaqColumnsParams): TableColumnsType<ISuggestedFaq> => [
  {
    title: (
      <StyledSearchWrapper>
        <span>{t('label_question_many')}</span>
        <Input
          defaultValue={filters?.question}
          onChange={(event) => onFilterChange('question', event.target.value)}
          size="small"
          placeholder={t('placeholder_search_question')}
        />
      </StyledSearchWrapper>
    ),
    dataIndex: 'question',
    key: 'question',
    width: 240,
    render: (question: string) => <StyledQuestion>{question}</StyledQuestion>,
  },
  {
    title: (
      <StyledSearchWrapper>
        <span>{t('label_answer_many')}</span>
        <Input
          defaultValue={filters?.answer}
          onChange={(event) => onFilterChange('answer', event.target.value)}
          size="small"
          placeholder={t('placeholder_search_answer')}
        />
      </StyledSearchWrapper>
    ),
    dataIndex: 'answer',
    key: 'answer',
    width: 240,
    render: (answer: string | null) => <StyledAnswer>{answer ?? '—'}</StyledAnswer>,
  },
  {
    title: t('label_updated_at'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    width: 130,
    render: (updatedAt: string) => <StyledDate>{renderDate(updatedAt, DateFormats.DATE_WITH_TIME)}</StyledDate>,
  },
  {
    title: (
      <StyledSearchWrapper>
        <span>{t('column_channel')}</span>
        <FaqChannelsSelect
          value={filters?.channelId}
          onChange={(channelId) => onFilterChange('channelId', channelId)}
          size="small"
          placeholder={t('column_channel')}
          allowClear
        />
      </StyledSearchWrapper>
    ),
    dataIndex: 'sourceChannels',
    key: 'sourceChannels',
    width: 200,
    render: (sourceChannels: ISuggestedFaqSourceChannel[]) =>
      sourceChannels.map((channel) => (
        <StyledChannelTag key={channel.channelId}>{channel.name || channel.slug}</StyledChannelTag>
      )),
  },
  {
    title: t('column_conversations'),
    dataIndex: 'occurrenceCount',
    key: 'occurrenceCount',
    width: 120,
    render: (occurrenceCount: number) => <strong>{occurrenceCount}</strong>,
  },
  {
    title: (
      <StyledSearchWrapper>
        <span>{t('label_status')}</span>
        <Select
          options={statusOptions}
          value={filters?.status}
          onChange={(status) => onFilterChange('status', status as SuggestedFaqStatus)}
          size="small"
          placeholder={t('label_status')}
          allowClear
        />
      </StyledSearchWrapper>
    ),
    dataIndex: 'status',
    key: 'status',
    width: 120,
    render: (status: SuggestedFaqStatus) => (
      <StatusPill status={status}>{getSuggestedFaqStatusLabel(status, t)}</StatusPill>
    ),
  },
  {
    title: t('column_action'),
    key: 'action',
    width: 80,
    render: (_, record) => (
      <Link to={`${ROUTES.SUGGESTED_FAQS}/${record.reviewId}`}>
        <Button type="primary">{t('view_btn')}</Button>
      </Link>
    ),
  },
];
